import * as React from "react";
import {
  makeStyles,
  Text,
  Button,
  Input,
  Dropdown,
  Option,
  Switch,
} from "@fluentui/react-components";
import {
  Add16Regular,
  Delete16Regular,
  ArrowUp16Regular,
  ArrowDown16Regular,
} from "@fluentui/react-icons";
import { SUB_WORKFLOWS } from "../../shared/workflowSchema";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: "12px" },
  intro: {
    fontSize: "12px",
    color: "var(--text-tertiary, #6E747D)",
    lineHeight: "1.45",
  },
  nameRow: { display: "flex", gap: "8px", alignItems: "center" },
  nodeList: { display: "flex", flexDirection: "column", gap: "8px" },
  node: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "10px",
    border: "1px solid var(--border-primary, #E0E2E6)",
    borderRadius: "8px",
    backgroundColor: "var(--bg-elevated, #FFFFFF)",
  },
  nodeHeader: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  nodeIdInput: { flex: 1 },
  nodeWfDropdown: { flex: 1 },
  nodeControls: { display: "flex", gap: "4px" },
  parallelToggle: { fontSize: "11px" },
  addBtn: {
    backgroundColor: "#FFB500",
    color: "#0B111A",
    fontWeight: "600",
    border: "none",
    ":hover": { backgroundColor: "#E6A300" },
  },
  saveBtn: {
    backgroundColor: "#FFB500",
    color: "#0B111A",
    fontWeight: "600",
    border: "none",
    ":hover": { backgroundColor: "#E6A300" },
  },
  jsonPreview: {
    fontSize: "11px",
    fontFamily: "monospace",
    padding: "8px",
    backgroundColor: "var(--bg-tertiary, #F0F1F3)",
    borderRadius: "4px",
    overflow: "auto",
    maxHeight: "120px",
    whiteSpace: "pre-wrap",
    color: "var(--text-secondary, #4A4F57)",
  },
  empty: {
    fontSize: "12px",
    color: "var(--text-tertiary, #6E747D)",
    textAlign: "center",
    padding: "12px",
  },
});

const WF_OPTIONS = Object.entries(SUB_WORKFLOWS).map(([k, v]) => ({
  key: v,
  label: v.replace("wf.", ""),
}));

/**
 * Compile the editor state into a Workflow {id, name, dag, output_cards}.
 *
 * Semantics:
 *   - Nodes flagged "parallel with previous" share parallel_group with the
 *     previous node (groups auto-numbered g1, g2, ...).
 *   - Sequential nodes get an `after` referencing the previous node id.
 *   - All node ids are output_cards (user picks via UI separately if needed).
 */
function compileDag(name, nodes) {
  const dag = [];
  let groupCounter = 0;
  let lastGroupId = null;
  let lastNodeIds = [];

  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    const node = { id: n.id || `n${i + 1}`, wf: n.wf };

    if (n.parallelWithPrev && i > 0) {
      // Stay in same wave as previous
      if (!lastGroupId) {
        groupCounter++;
        lastGroupId = `g${groupCounter}`;
        // Stamp the previous node into this group too
        const prev = dag[dag.length - 1];
        prev.parallel_group = lastGroupId;
        delete prev.after;
        lastNodeIds = [prev.id];
      }
      node.parallel_group = lastGroupId;
      lastNodeIds.push(node.id);
    } else {
      // Sequential — depend on all of last wave (ensures correct order)
      if (lastNodeIds.length > 0) node.after = [...lastNodeIds];
      lastNodeIds = [node.id];
      lastGroupId = null;
    }

    dag.push(node);
  }

  return {
    id: `custom-${Date.now()}`,
    name: name || "Custom workflow",
    dag,
    output_cards: dag.map((n) => n.id),
  };
}

/**
 * WorkflowBuilder — minimal form-based DAG composer.
 *
 * @param {{
 *   initialName?: string,
 *   initialNodes?: Array<{id,wf,parallelWithPrev}>,
 *   onSave: (workflow) => void,
 *   onCancel?: () => void,
 * }} props
 */
const WorkflowBuilder = ({ initialName = "", initialNodes = [], onSave, onCancel }) => {
  const styles = useStyles();
  const [name, setName] = React.useState(initialName);
  const [nodes, setNodes] = React.useState(
    initialNodes.length
      ? initialNodes
      : [{ id: "n1", wf: SUB_WORKFLOWS.SUMMARIZE, parallelWithPrev: false }]
  );

  const compiled = React.useMemo(() => compileDag(name, nodes), [name, nodes]);

  const updateNode = (idx, patch) => {
    setNodes((prev) => prev.map((n, i) => (i === idx ? { ...n, ...patch } : n)));
  };

  const addNode = () => {
    setNodes((prev) => [
      ...prev,
      { id: `n${prev.length + 1}`, wf: SUB_WORKFLOWS.SUMMARIZE, parallelWithPrev: false },
    ]);
  };

  const removeNode = (idx) => {
    setNodes((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveNode = (idx, dir) => {
    setNodes((prev) => {
      const copy = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= copy.length) return copy;
      [copy[idx], copy[target]] = [copy[target], copy[idx]];
      return copy;
    });
  };

  return (
    <div className={styles.root}>
      <Text className={styles.intro}>
        Build a custom workflow. Steps run sequentially by default. Toggle
        &quot;parallel with previous&quot; to fan-out within the same wave.
      </Text>

      <div className={styles.nameRow}>
        <Input
          placeholder="Workflow name (e.g. Polish round-trip)"
          value={name}
          onChange={(_, d) => setName(d.value)}
          style={{ flex: 1 }}
        />
      </div>

      <div className={styles.nodeList}>
        {nodes.length === 0 && (
          <Text className={styles.empty}>No steps yet. Add one below.</Text>
        )}
        {nodes.map((n, idx) => (
          <div key={idx} className={styles.node}>
            <div className={styles.nodeHeader}>
              <Input
                size="small"
                className={styles.nodeIdInput}
                placeholder={`step-${idx + 1}`}
                value={n.id}
                onChange={(_, d) => updateNode(idx, { id: d.value })}
              />
              <Dropdown
                size="small"
                className={styles.nodeWfDropdown}
                value={n.wf.replace("wf.", "")}
                onOptionSelect={(_, d) => updateNode(idx, { wf: d.optionValue })}
              >
                {WF_OPTIONS.map((opt) => (
                  <Option key={opt.key} value={opt.key}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
              <div className={styles.nodeControls}>
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<ArrowUp16Regular />}
                  onClick={() => moveNode(idx, -1)}
                  disabled={idx === 0}
                  aria-label="Move up"
                />
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<ArrowDown16Regular />}
                  onClick={() => moveNode(idx, 1)}
                  disabled={idx === nodes.length - 1}
                  aria-label="Move down"
                />
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<Delete16Regular />}
                  onClick={() => removeNode(idx)}
                  aria-label="Remove"
                />
              </div>
            </div>
            {idx > 0 && (
              <Switch
                className={styles.parallelToggle}
                label="Run in parallel with previous step"
                checked={!!n.parallelWithPrev}
                onChange={(_, d) => updateNode(idx, { parallelWithPrev: d.checked })}
              />
            )}
          </div>
        ))}
      </div>

      <Button
        appearance="primary"
        className={styles.addBtn}
        icon={<Add16Regular />}
        onClick={addNode}
      >
        Add step
      </Button>

      <div className={styles.jsonPreview}>{JSON.stringify(compiled.dag, null, 2)}</div>

      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
        {onCancel && (
          <Button appearance="subtle" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          appearance="primary"
          className={styles.saveBtn}
          onClick={() => onSave(compiled)}
          disabled={!name.trim() || nodes.length === 0}
        >
          Save workflow
        </Button>
      </div>
    </div>
  );
};

export default WorkflowBuilder;
